import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CreditCard, Loader2, CheckCircle2, FileDown, Ban } from "lucide-react";
import { api, API, formatApiError } from "@/lib/auth";
import { fmtDate } from "@/lib/datetime";

function eurFr(n) {
  const v = Number(n || 0);
  return Math.abs(v - Math.round(v)) < 0.005 ? `${Math.round(v)} €` : `${v.toFixed(2).replace(".", ",")} €`;
}

const CLOSED = {
  revoked: "Ce lien de règlement a été désactivé. Merci de contacter Charbonier Studio.",
  expired: "Ce lien de règlement a expiré. Merci de contacter Charbonier Studio pour en recevoir un nouveau.",
};

export default function PayLink() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [cgv, setCgv] = useState(false);
  const sessionId = params.get("session_id");

  useEffect(() => {
    const q = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : "";
    api.get(`/pay/${token}${q}`).then((r) => setInfo(r.data)).catch((e) => setError(formatApiError(e)));
  }, [token, sessionId]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pay/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { setError(formatApiError(e)); setBusy(false); }
  };

  return (
    <div className="cs-cream min-h-[80vh] py-16" data-testid="page-pay-link">
      <div className="max-w-lg mx-auto px-6">
        <div className="cs-eyebrow text-[#0E7C90]">Charbonier Studio · Règlement</div>
        {!info && !error && <div className="mt-8 flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Chargement…</div>}
        {error && <div className="mt-8 cs-card p-6 text-sm text-red-700" role="alert" data-testid="pay-link-error">{error}</div>}
        {info && (
          <div className="mt-6 cs-card p-6" data-testid="pay-link-card">
            <h1 className="cs-h1 text-3xl">{info.service_name}</h1>
            <div className="mt-4 text-sm space-y-1.5">
              <div className="flex justify-between border-b border-black/10 py-1.5"><span className="text-neutral-500">Commande</span><span className="font-mono" data-testid="pay-link-reference">{info.reference}</span></div>
              {info.billed_to && <div className="flex justify-between border-b border-black/10 py-1.5"><span className="text-neutral-500">Facturé à</span><span data-testid="pay-link-billed-to">{info.billed_to}</span></div>}
              <div className="flex justify-between border-b border-black/10 py-1.5"><span className="text-neutral-500">{info.step_label}</span><span className="font-mono text-lg" data-testid="pay-link-amount">{eurFr(info.amount)}</span></div>
            </div>
            {info.state === "active" && (
              <>
                {params.get("annule") && <p className="mt-4 text-[13px] text-amber-700">Paiement annulé. Vous pouvez réessayer.</p>}
                <label className="mt-5 flex items-start gap-2 text-[13px]">
                  <input type="checkbox" checked={cgv} onChange={(e) => setCgv(e.target.checked)} className="mt-1" data-testid="pay-link-cgv" />
                  <span>J'accepte les <a href="/cgv" target="_blank" rel="noreferrer" className="cs-link-underline text-[#0E7C90]">conditions générales de vente</a>.</span>
                </label>
                <button onClick={pay} disabled={busy || !cgv} className="cs-btn-primary w-full justify-center mt-4 disabled:opacity-50" data-testid="pay-link-pay">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} Payer {eurFr(info.amount)} par carte
                </button>
                <p className="mt-3 text-[11px] font-mono text-neutral-500">Lien valable jusqu'au {fmtDate(info.expires_at)}. Paiement sécurisé par Stripe. Virement possible sur demande.</p>
              </>
            )}
            {info.state === "paid" && (
              <div className="mt-5" data-testid="pay-link-paid">
                <p className="text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 size={16} /> Règlement reçu, merci.</p>
                {info.invoice_number
                  ? <a href={`${API}/pay/${token}/invoice.pdf`} className="cs-btn-ghost !text-black !border-black mt-4 inline-flex" data-testid="pay-link-invoice"><FileDown size={14} /> <span>Facture {info.invoice_number}</span></a>
                  : <p className="mt-2 text-[12px] font-mono text-neutral-500">Votre facture est en cours d'émission.</p>}
              </div>
            )}
            {CLOSED[info.state] && <p className="mt-5 text-sm text-neutral-700 inline-flex items-start gap-2" data-testid="pay-link-closed"><Ban size={15} className="mt-0.5" /> {CLOSED[info.state]}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
